import {
  Close,
  FilterList,
  GetApp,
  Search,
  ViewColumn,
} from '@material-ui/icons';
import { ChangeEvent, useState } from 'react';
import { ColumnFilterMenu } from './ColumnFilterMenu';
import { CsvDownloadMenu } from './CsvDownloadMenu';
import { FilterDataMenu } from './FilterDataMenu';
import { Tooltip } from '../Tooltip';
import { IconButton } from '../IconButton';
import { Input } from '../Input';

export const Toolbar = (props: ToolbarProps) => {
  const {
    csvName,
    data,
    displayData,
    expandSearch,
    filteredColumns,
    filteredData,
    formatters,
    loading,
    searchValue,
    setFilteredColumns,
    setFilteredData,
    setSearchValue,
    tools,
  } = props;
  const [searching, setSearching] = useState(expandSearch);
  const [anchorElement, setAnchorElement] = useState<Element | undefined>(
    undefined
  );
  const [iconClicked, setIconClicked] = useState('');

  const handleInputCloseClick = () => {
    resetAnchorState();
    if (searchValue) {
      setSearchValue('');
    } else {
      setSearching(false);
    }
  };

  const resetAnchorState = () => {
    setAnchorElement(undefined);
    setIconClicked('');
  };

  const setAnchorState = (eventTarget: Element | undefined, icon: string) => {
    setAnchorElement(eventTarget);
    setIconClicked(icon);
  };

  return (
    <>
      <div className="flex flex-row justify-end items-center px-1 py-3 gap-1 min-h-[64px]">
        {tools === undefined || tools?.includes('search') ? (
          searching ? (
            <Input
              autoFocus
              endAdornment={
                <Tooltip title={searchValue ? 'Clear Search' : 'Close Search'}>
                  <button onClick={handleInputCloseClick}>
                    <Close fontSize="small" />
                  </button>
                </Tooltip>
              }
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setSearchValue(event.target.value)
              }
              placeholder="Search"
              variant="contained"
              value={searchValue}
            />
          ) : (
            <Tooltip title="Search">
              <IconButton
                onClick={() => {
                  resetAnchorState();
                  setSearching(true);
                }}
                fontSize="sm"
              >
                <Search />
              </IconButton>
            </Tooltip>
          )
        ) : null}

        {tools === undefined || tools?.includes('csv') ? (
          <Tooltip title="Download CSV">
            <IconButton
              disabled={loading}
              onClick={(event: any) => {
                if (anchorElement !== event.currentTarget)
                  setAnchorState(event.currentTarget, 'csv');
                else resetAnchorState();
              }}
              fontSize="sm"
            >
              <GetApp />
            </IconButton>
          </Tooltip>
        ) : null}

        {tools === undefined || tools?.includes('columns') ? (
          <Tooltip title="Show/Hide Columns">
            {!filteredColumns.length ? (
              <span>
                <IconButton
                  disabled={!filteredColumns.length}
                  onClick={(event: any) => {
                    if (anchorElement !== event.currentTarget)
                      setAnchorState(event.currentTarget, 'column');
                    else resetAnchorState();
                  }}
                  fontSize="sm"
                >
                  <ViewColumn />
                </IconButton>
              </span>
            ) : (
              <IconButton
                onClick={(event: any) => {
                  if (anchorElement !== event.currentTarget)
                    setAnchorState(event.currentTarget, 'column');
                  else resetAnchorState();
                }}
                fontSize="sm"
              >
                <ViewColumn />
              </IconButton>
            )}
          </Tooltip>
        ) : null}

        {tools === undefined || tools?.includes('filter') ? (
          <Tooltip title="Filters">
            {!filteredColumns.filter((c) => c.canFilter).length ? (
              <span>
                <IconButton
                  disabled={!filteredColumns.filter((c) => c.canFilter).length}
                  onClick={(event: any) => {
                    if (anchorElement !== event.currentTarget)
                      setAnchorState(event.currentTarget, 'data');
                    else resetAnchorState();
                  }}
                  fontSize="sm"
                >
                  <FilterList />
                </IconButton>
              </span>
            ) : (
              <IconButton
                onClick={(event: any) => {
                  if (anchorElement !== event.currentTarget)
                    setAnchorState(event.currentTarget, 'data');
                  else resetAnchorState();
                }}
                fontSize="sm"
              >
                <FilterList />
              </IconButton>
            )}
          </Tooltip>
        ) : null}
      </div>

      <ColumnFilterMenu
        anchorElement={anchorElement}
        filteredColumns={filteredColumns}
        loading={loading}
        onClose={() => setAnchorState(undefined, '')}
        open={iconClicked === 'column'}
        setFilteredColumns={setFilteredColumns}
      />
      <CsvDownloadMenu
        anchorElement={anchorElement}
        csvName={csvName}
        data={data}
        displayData={displayData}
        filteredColumns={filteredColumns}
        filteredData={filteredData}
        formatters={formatters}
        onClose={() => setAnchorState(undefined, '')}
        open={iconClicked === 'csv'}
      />
      <FilterDataMenu
        anchorElement={anchorElement}
        loading={loading}
        onClose={() => setAnchorState(undefined, '')}
        data={data}
        filteredColumns={filteredColumns}
        open={iconClicked === 'data'}
        setFilteredData={setFilteredData}
      />
    </>
  );
};
